<template>
  <div>
    <div v-if="useMultipleDiners">
      <div
        v-for="(personData, pIndex) in personsWithGrouping"
        :key="pIndex"
        class="mb-4"
      >
        <div class="flex items-center justify-between mb-2 bg-indigo-50 dark:bg-indigo-900/20 px-3 py-2 rounded-md">
          <h5 class="text-sm font-semibold text-indigo-900 dark:text-indigo-100">
            {{ personData.name || `Persona ${personData.position}` }}
          </h5>
          <span class="text-xs text-indigo-600 dark:text-indigo-400">
            {{ personData.totalItemCount }} {{ personData.totalItemCount === 1 ? 'item' : 'items' }}
            <span
              v-if="personData.groupCount !== personData.totalItemCount"
              class="text-indigo-500"
            >
              ({{ personData.groupCount }} {{ personData.groupCount === 1 ? 'grupo' : 'grupos' }})
            </span>
          </span>
        </div>
        <div
          v-if="personData.groupedItems.length > 0"
          class="space-y-2 ml-2"
        >
          <div
            v-for="(group, gIndex) in personData.groupedItems"
            :key="gIndex"
            class="flex justify-between items-start text-sm py-2 border-b border-gray-100 dark:border-gray-800 last:border-0"
          >
            <div class="flex-1">
              <p class="font-medium text-gray-900 dark:text-gray-100">
                {{ group.name }}
                <span
                  v-if="group.variant_name"
                  class="text-xs text-gray-500 ml-1"
                >({{ group.variant_name }})</span>
                <span
                  v-if="group.item_type === 'takeaway'" 
                  class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ml-1 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300"
                >
                  Para llevar
                </span>
              </p>
              <div
                v-if="group.category"
                class="mt-1"
              >
                <span
                  class="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300"
                >
                  {{ group.category }}
                </span>
              </div>
              <div
                v-if="!isPosOnlyMode && group.special_instructions"
                class="mt-1 space-y-0.5"
              >
                <p
                  v-for="(instruction, idx) in getFormattedInstructions(group)"
                  :key="idx" 
                  class="text-xs text-gray-500 dark:text-gray-400"
                >
                  {{ instruction }}
                </p>
              </div>
            </div>

            <div class="flex flex-col items-end space-y-0.5">
              <div class="flex items-center space-x-3">
                <span class="text-gray-700 dark:text-gray-200 text-xs font-medium">{{ group.quantity }}x</span>
                <span class="font-medium text-gray-900 dark:text-gray-100 min-w-[60px] text-right">
                  ${{ (group.price * group.quantity).toFixed(2) }}
                </span>
              </div>
              <span
                v-if="group.quantity > 1"
                class="text-xs text-gray-500 dark:text-gray-400"
              >
                ${{ group.price.toFixed(2) }} c/u
              </span>
            </div>
          </div>
        </div>
        <div
          v-else
          class="ml-2 py-3 text-sm text-gray-500 dark:text-gray-400 italic"
        >
          Sin items agregados
        </div>
      </div>
    </div>

    <div
      v-else
      class="space-y-3"
    >
      <div
        v-for="item in selectedItems"
        :key="item.id"
        class="flex justify-between items-start"
      >
        <div class="flex-1">
          <div class="flex items-center gap-2">
            <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
              {{ item.name }}
              <span
                v-if="item.variant_name"
                class="text-xs text-gray-500 ml-1"
              >({{ item.variant_name }})</span>
            </p>
            <span
              v-if="isItemLocked(item)"
              class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
              :class="{
                'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200': item.status === 'preparing',
                'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200': item.status === 'ready'
              }"
            >
              {{ $t(`app.status.${item.status}`) }}
            </span>
          </div>
          <div
            v-if="item.category"
            class="mt-1"
          >
            <span
              class="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300"
            >
              {{ item.category }}
            </span>
          </div>
          <p
            v-if="!isPosOnlyMode && item.notes"
            class="text-xs text-gray-500 dark:text-gray-400 mt-1"
          >
            {{ item.notes }}
          </p>
          <div
            v-if="item.extras && item.extras.length > 0"
            class="mt-1 space-y-0.5"
          >
            <p
              v-for="(extra, idx) in item.extras"
              :key="idx"
              class="text-xs text-indigo-600 dark:text-indigo-400"
            >
              + {{ extra.name }} ({{ extra.quantity }}x ${{ extra.price.toFixed(2) }})
            </p>
          </div>
          <p
            v-if="isItemLocked(item)"
            class="text-xs text-orange-600 dark:text-orange-400 mt-1"
          >
            {{ $t('app.views.orders.modals.new_order.item_locked') }}
          </p>
        </div>
        <div class="flex items-center space-x-4 ml-4">
          <div class="flex items-center space-x-2">
            <button
              type="button"
              class="p-1 text-gray-500 hover:text-indigo-600 focus:outline-none disabled:opacity-30 disabled:cursor-not-allowed"
              :disabled="isItemLocked(item)"
              @click.stop="$emit('decrease-quantity', item)"
            >
              <MinusIcon class="h-4 w-4" />
            </button>
            <span class="text-sm text-gray-700 dark:text-gray-200 w-6 text-center">{{ item.quantity }}</span>
            <button
              type="button"
              class="p-1 text-gray-500 hover:text-indigo-600 focus:outline-none disabled:opacity-30 disabled:cursor-not-allowed"
              :disabled="isItemLocked(item)"
              @click.stop="$emit('increase-quantity', item)"
            >
              <PlusIcon class="h-4 w-4" />
            </button>
          </div>
          <span class="text-sm font-medium text-gray-900 dark:text-gray-100 w-16 text-right">
            ${{ calculateItemTotal(item) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { GroupedOrderItem } from '@/utils/orderItemGrouping';
import { formatSpecialInstructions } from '@/utils/orderItemGrouping';
import { MinusIcon, PlusIcon } from '@heroicons/vue/24/outline';
import { useOperationMode } from '@/composables/useOperationMode';
import type { DisplayOrderItem, PersonWithGrouping } from './orderSummaryTypes';

interface Props {
  useMultipleDiners: boolean;
  personsWithGrouping: PersonWithGrouping[];
  selectedItems: DisplayOrderItem[];
  getMenuItemName: (itemId: number) => string;
  getMenuItemCategory?: (itemId: number) => string;
  calculateItemTotal: (item: DisplayOrderItem) => string;
  isItemLocked: (item: DisplayOrderItem) => boolean;
}

defineProps<Props>();

defineEmits<{
  (e: 'decrease-quantity', item: DisplayOrderItem): void;
  (e: 'increase-quantity', item: DisplayOrderItem): void;
}>();

const { t } = useI18n();
const { isPosOnlyMode } = useOperationMode();

const getFormattedInstructions = (group: GroupedOrderItem) => formatSpecialInstructions(group.special_instructions);
</script>
